import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { alpha } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import MapIcon from "@mui/icons-material/Map";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DataGrid } from "@mui/x-data-grid";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import AppNavbar from "../components/AppNavbar";
import Header from "../components/Header";
import SideMenu from "../components/SideMenu";
import AppTheme from "../shared-theme/AppTheme";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from "../theme/customizations";
import PageErrorBoundary from "../components/PageErrorBoundary";
import { useTranslation } from "react-i18next";
import { getNode, updateNode } from "../api/nodeAPI";
import { getWebUsers } from "../api/webUserAPI";
import { calculateRoute } from "../api/routingAPI";

// Leaflet default marker icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng);
    },
  });
  return null;
}

function MapCenterUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom);
    }
  }, [map, center, zoom]);
  return null;
}

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const stepIcons = {
  0: <PointOfSaleIcon />,
  1: <PeopleIcon />,
  2: <AltRouteIcon />,
};

function StepIconComponent({ active, completed, icon }) {
  const idx = Number(icon) - 1;
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: completed
          ? "success.main"
          : active
            ? "primary.main"
            : "action.disabledBackground",
        color: completed || active ? "white" : "text.disabled",
        transition: "all 0.3s ease",
      }}
    >
      {completed ? <CheckCircleOutlineIcon fontSize="small" /> : stepIcons[idx]}
    </Box>
  );
}

export default function CreateRoute(props) {
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const [nodes, setNodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userLocations, setUserLocations] = useState({});
  const [editingUserId, setEditingUserId] = useState(null);
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [routeResult, setRouteResult] = useState(null);
  const [tempUserDialog, setTempUserDialog] = useState(false);
  const [tempUserName, setTempUserName] = useState("");
  const [tempUserCounter, setTempUserCounter] = useState(0);

  // Location dialog state
  const [locationDialog, setLocationDialog] = useState({
    open: false,
    node: null,
  });
  const [editLat, setEditLat] = useState("");
  const [editLng, setEditLng] = useState("");

  // Map dialog state
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 39.0, lng: 35.0 });
  const [mapZoom, setMapZoom] = useState(6);
  const [tempMarker, setTempMarker] = useState(null);
  const [mapContext, setMapContext] = useState("node");

  const steps = [
    t("createRoute.selectNodes"),
    t("createRoute.selectUser"),
    t("createRoute.create"),
  ];

  // Fetch nodes on mount
  useEffect(() => {
    const fetchNodes = async () => {
      try {
        setLoadingNodes(true);
        const result = await getNode();
        setNodes(result || []);
      } catch (err) {
        console.error("Error fetching nodes:", err);
      } finally {
        setLoadingNodes(false);
      }
    };
    fetchNodes();
  }, []);

  // Fetch users when moving to step 2
  useEffect(() => {
    if (activeStep === 1 && users.length === 0) {
      const fetchUsers = async () => {
        try {
          setLoadingUsers(true);
          const result = await getWebUsers();
          setUsers(result || []);
        } catch (err) {
          console.error("Error fetching users:", err);
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [activeStep, users.length]);

  const handleAddTempUser = () => {
    if (!tempUserName.trim()) return;
    const tempId = `temp_${tempUserCounter}`;
    setTempUserCounter((c) => c + 1);
    const newUser = {
      id: tempId,
      username: tempUserName.trim(),
      firstName: tempUserName.trim(),
      lastName: "",
      _isTemp: true,
    };
    setUsers((prev) => [newUser, ...prev]);
    setSelectedUserIds((prev) => [...prev, tempId]);
    setTempUserName("");
    setTempUserDialog(false);
  };

  // All selected users must have a location to proceed
  const allUsersHaveLocation = selectedUserIds.length > 0 &&
    selectedUserIds.every((id) => {
      const loc = userLocations[id];
      return loc?.lat && loc?.lng;
    });

  const handleOpenLocationDialog = (node) => {
    setEditLat(node.latitude || "");
    setEditLng(node.longitude || "");
    setLocationDialog({ open: true, node });
  };

  const handleSaveLocation = async () => {
    const { node } = locationDialog;
    if (!node) return;
    try {
      await updateNode({
        ...node,
        latitude: editLat || null,
        longitude: editLng || null,
      });
      setNodes((prev) =>
        prev.map((n) =>
          n.idNode === node.idNode
            ? { ...n, latitude: editLat || null, longitude: editLng || null }
            : n,
        ),
      );
      setLocationDialog({ open: false, node: null });
    } catch (err) {
      console.error("Error updating location:", err);
    }
  };

  // Map dialog handlers
  const handleOpenMap = useCallback(() => {
    setMapContext("node");
    const lat = parseFloat(editLat);
    const lng = parseFloat(editLng);
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });
      setMapZoom(13);
      setTempMarker({ lat, lng });
    } else {
      setMapCenter({ lat: 39.0, lng: 35.0 });
      setMapZoom(6);
      setTempMarker(null);
    }
    setMapDialogOpen(true);
  }, [editLat, editLng]);

  const handleOpenUserMap = useCallback((userId) => {
    setMapContext("user");
    setEditingUserId(userId);
    const loc = userLocations[userId];
    const lat = parseFloat(loc?.lat);
    const lng = parseFloat(loc?.lng);
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });
      setMapZoom(13);
      setTempMarker({ lat, lng });
    } else {
      setMapCenter({ lat: 39.0, lng: 35.0 });
      setMapZoom(6);
      setTempMarker(null);
    }
    setMapDialogOpen(true);
  }, [userLocations]);

  const handleMapClick = useCallback((latlng) => {
    setTempMarker({ lat: latlng.lat, lng: latlng.lng });
  }, []);

  const handleConfirmMapLocation = useCallback(() => {
    if (tempMarker) {
      if (mapContext === "node") {
        setEditLat(String(tempMarker.lat.toFixed(6)));
        setEditLng(String(tempMarker.lng.toFixed(6)));
      } else if (editingUserId) {
        setUserLocations((prev) => ({
          ...prev,
          [editingUserId]: {
            lat: String(tempMarker.lat.toFixed(6)),
            lng: String(tempMarker.lng.toFixed(6)),
          },
        }));
      }
    }
    setMapDialogOpen(false);
    setEditingUserId(null);
  }, [tempMarker, mapContext, editingUserId]);

  const nodeColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("createRoute.nodeName"),
        flex: 1,
        minWidth: 180,
      },
      {
        field: "latitude",
        headerName: t("createRoute.latitude"),
        width: 130,
        renderCell: (params) => (
          <Typography variant="body2" color={params.value ? "text.primary" : "text.disabled"}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "longitude",
        headerName: t("createRoute.longitude"),
        width: 130,
        renderCell: (params) => (
          <Typography variant="body2" color={params.value ? "text.primary" : "text.disabled"}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "location",
        headerName: t("createRoute.location"),
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const hasLocation = params.row.latitude && params.row.longitude;
          return hasLocation ? (
            <Chip
              icon={<LocationOnIcon sx={{ fontSize: 16 }} />}
              label={t("createRoute.hasLocation")}
              color="success"
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<AddLocationIcon sx={{ fontSize: 16 }} />}
              label={t("createRoute.addLocation")}
              size="small"
              color="primary"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenLocationDialog(params.row);
              }}
              sx={{ cursor: "pointer" }}
            />
          );
        },
      },
    ],
    [t],
  );

  const userColumns = useMemo(
    () => [
      {
        field: "username",
        headerName: t("createRoute.username"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "firstName",
        headerName: t("createRoute.firstName"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "lastName",
        headerName: t("createRoute.lastName"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "location",
        headerName: t("createRoute.location"),
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const loc = userLocations[params.row.id];
          const hasLoc = loc?.lat && loc?.lng;
          return hasLoc ? (
            <Chip
              icon={<LocationOnIcon sx={{ fontSize: 16 }} />}
              label={`${loc.lat}, ${loc.lng}`}
              color="success"
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenUserMap(params.row.id);
              }}
              sx={{ maxWidth: 155, fontSize: "0.7rem", cursor: "pointer" }}
            />
          ) : (
            <Chip
              icon={<MapIcon sx={{ fontSize: 16 }} />}
              label={t("createRoute.selectFromMap")}
              size="small"
              color="primary"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenUserMap(params.row.id);
              }}
              sx={{ cursor: "pointer" }}
            />
          );
        },
      },
    ],
    [t, userLocations, handleOpenUserMap],
  );

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreateRoute = async () => {
    const selectedNodes = nodes.filter((n) =>
      selectedNodeIds.includes(n.idNode),
    );
    const selectedUsers = users.filter((u) =>
      selectedUserIds.includes(u.id),
    );
    if (selectedUsers.length === 0 || selectedNodes.length === 0) return;

    const payload = {
      criticalMachines: selectedNodes.map((node) => ({
        name: node.name,
        latitude: parseFloat(node.latitude) || 0,
        longitude: parseFloat(node.longitude) || 0,
        currentStock: 5,
        maxStock: 50,
      })),
      routmen: selectedUsers.map((user) => {
        const loc = userLocations[user.id];
        return {
          fullName: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username,
          latitude: parseFloat(loc?.lat) || 0,
          longitude: parseFloat(loc?.lng) || 0,
        };
      }),
    };

    try {
      setCreating(true);
      const result = await calculateRoute(payload);
      // result is the data array from interceptor, or response.data
      const data = Array.isArray(result) ? result : result?.data || result;
      setRouteResult(Array.isArray(data) ? data : [data]);
      setActiveStep(3);
    } catch (err) {
      console.error("Error calculating route:", err);
    } finally {
      setCreating(false);
    }
  };

  const allNodesHaveLocation =
    selectedNodeIds.length > 0 &&
    selectedNodeIds.every((id) => {
      const node = nodes.find((n) => n.idNode === id);
      return node?.latitude && node?.longitude;
    });

  const isNextDisabled =
    (activeStep === 0 && (selectedNodeIds.length === 0 || !allNodesHaveLocation)) ||
    (activeStep === 1 && !allUsersHaveLocation);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: "flex" }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: "auto",
          })}
        >
          <PageErrorBoundary>
            <Stack
              spacing={2}
              sx={{
                alignItems: "center",
                mx: 3,
                pb: 5,
                mt: { xs: 8, md: 0 },
              }}
            >
              <Header />

              <Box sx={{ width: "100%", maxWidth: 1000 }}>
                {/* Stepper */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                  >
                    {steps.map((label, idx) => (
                      <Step key={label}>
                        <StepLabel StepIconComponent={(props) => <StepIconComponent {...props} icon={idx + 1} />}>
                          <Typography
                            variant="body2"
                            fontWeight={activeStep === idx ? 600 : 400}
                            color={activeStep === idx ? "text.primary" : "text.secondary"}
                          >
                            {label}
                          </Typography>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Paper>

                {/* Step Content Card */}
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                    mb: 3,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Step 0: Node Selection */}
                    {activeStep === 0 && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                          <PointOfSaleIcon color="primary" />
                          <Typography variant="h6">
                            {t("createRoute.selectNodesTitle")}
                          </Typography>
                          {selectedNodeIds.length > 0 && (
                            <Chip
                              label={`${selectedNodeIds.length} ${t("createRoute.nodeCount")}`}
                              size="small"
                              color="primary"
                            />
                          )}
                        </Box>
                        {loadingNodes ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              py: 6,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : (
                          <DataGrid
                            rows={nodes}
                            columns={nodeColumns}
                            getRowId={(row) => row.idNode}
                            checkboxSelection
                            rowSelectionModel={selectedNodeIds}
                            onRowSelectionModelChange={(newSelection) =>
                              setSelectedNodeIds(newSelection)
                            }
                            initialState={{
                              pagination: { paginationModel: { pageSize: 10 } },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            autoHeight
                            disableRowSelectionOnClick
                            sx={{
                              border: 0,
                              "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "action.hover",
                                borderRadius: 1,
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Step 1: User Selection */}
                    {activeStep === 1 && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                          <PeopleIcon color="primary" />
                          <Typography variant="h6">
                            {t("createRoute.selectUserTitle")}
                          </Typography>
                          {selectedUserIds.length > 0 && (
                            <Chip
                              label={`${selectedUserIds.length} ${t("createRoute.userCount")}`}
                              size="small"
                              color="primary"
                            />
                          )}
                          <Box sx={{ flexGrow: 1 }} />
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PersonAddIcon />}
                            onClick={() => setTempUserDialog(true)}
                          >
                            {t("createRoute.addUser")}
                          </Button>
                        </Box>
                        {loadingUsers ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              py: 6,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : (
                          <DataGrid
                            rows={users}
                            columns={userColumns}
                            getRowId={(row) => row.id}
                            checkboxSelection
                            rowSelectionModel={selectedUserIds}
                            onRowSelectionModelChange={(newSelection) =>
                              setSelectedUserIds(newSelection)
                            }
                            initialState={{
                              pagination: { paginationModel: { pageSize: 10 } },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            autoHeight
                            disableRowSelectionOnClick
                            sx={{
                              border: 0,
                              "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "action.hover",
                                borderRadius: 1,
                              },
                            }}
                          />
                        )}

                        {/* Warning: users without location */}
                        {selectedUserIds.length > 0 && !allUsersHaveLocation && (
                          <Paper
                            sx={{
                              mt: 1.5,
                              p: 1.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              backgroundColor: "warning.lighter",
                              border: 1,
                              borderColor: "warning.main",
                              borderRadius: 1.5,
                            }}
                          >
                            <WarningAmberIcon color="warning" fontSize="small" />
                            <Typography variant="body2" color="warning.dark">
                              {t("createRoute.locationRequired")}
                            </Typography>
                          </Paper>
                        )}
                      </Box>
                    )}

                    {/* Step 2: Summary */}
                    {activeStep === 2 && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                          <AltRouteIcon color="primary" />
                          <Typography variant="h6">
                            {t("createRoute.summary")}
                          </Typography>
                        </Box>

                        {/* Nodes Summary */}
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, mb: 2, borderRadius: 2 }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                            <PointOfSaleIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="text.secondary">
                              {t("createRoute.selectedNodes")}
                            </Typography>
                            <Chip
                              label={selectedNodeIds.length}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {nodes
                              .filter((n) => selectedNodeIds.includes(n.idNode))
                              .map((n) => (
                                <Chip
                                  key={n.idNode}
                                  icon={<PointOfSaleIcon sx={{ fontSize: 14 }} />}
                                  label={n.name}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                          </Box>
                        </Paper>

                        {/* Users Summary */}
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 2 }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                            <PeopleIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="text.secondary">
                              {t("createRoute.selectedUser")}
                            </Typography>
                            <Chip
                              label={selectedUserIds.length}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <Stack spacing={1}>
                            {users
                              .filter((u) => selectedUserIds.includes(u.id))
                              .map((u) => {
                                const loc = userLocations[u.id];
                                const name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
                                return (
                                  <Box
                                    key={u.id}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Chip
                                      icon={<PersonIcon sx={{ fontSize: 14 }} />}
                                      label={name}
                                      size="small"
                                      variant="outlined"
                                    />
                                    {loc?.lat && loc?.lng && (
                                      <Chip
                                        icon={<LocationOnIcon sx={{ fontSize: 14 }} />}
                                        label={`${loc.lat}, ${loc.lng}`}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{ fontSize: "0.7rem" }}
                                      />
                                    )}
                                  </Box>
                                );
                              })}
                          </Stack>
                        </Paper>
                      </Box>
                    )}

                    {/* Step 3: Route Result */}
                    {activeStep === 3 && routeResult && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                          <CheckCircleOutlineIcon color="success" />
                          <Typography variant="h6">
                            {t("createRoute.routeCreated")}
                          </Typography>
                        </Box>

                        <Stack spacing={2}>
                          {routeResult.map((route, idx) => {
                            const totalMin = Math.round((route.totalDuration || 0) / 60);
                            const hours = Math.floor(totalMin / 60);
                            const mins = totalMin % 60;
                            const durationText = hours > 0
                              ? `${hours} ${t("createRoute.hours")} ${mins} ${t("createRoute.minutes")}`
                              : `${mins} ${t("createRoute.minutes")}`;

                            return (
                              <Paper
                                key={idx}
                                variant="outlined"
                                sx={{ borderRadius: 2, overflow: "hidden" }}
                              >
                                {/* Route header */}
                                <Box
                                  sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    backgroundColor: "primary.main",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <PersonIcon />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {route.routmanName}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <AccessTimeIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {durationText}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ p: 2.5 }}>
                                  {/* Stops */}
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    {t("createRoute.routeStops")}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      mb: 2.5,
                                    }}
                                  >
                                    {(route.stops || []).map((stop, sIdx) => (
                                      <React.Fragment key={sIdx}>
                                        <Chip
                                          icon={
                                            sIdx === 0 || sIdx === (route.stops || []).length - 1
                                              ? <PersonIcon sx={{ fontSize: 14 }} />
                                              : <PointOfSaleIcon sx={{ fontSize: 14 }} />
                                          }
                                          label={stop}
                                          size="small"
                                          color={
                                            sIdx === 0 || sIdx === (route.stops || []).length - 1
                                              ? "primary"
                                              : "default"
                                          }
                                          variant={
                                            sIdx === 0 || sIdx === (route.stops || []).length - 1
                                              ? "filled"
                                              : "outlined"
                                          }
                                        />
                                        {sIdx < (route.stops || []).length - 1 && (
                                          <ArrowRightAltIcon
                                            fontSize="small"
                                            color="action"
                                          />
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </Box>

                                  {/* Google Maps Link */}
                                  {route.googleMapsLink && (
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      startIcon={<MapIcon />}
                                      endIcon={<OpenInNewIcon />}
                                      href={route.googleMapsLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      fullWidth
                                      sx={{ borderRadius: 1.5, textTransform: "none" }}
                                    >
                                      {t("createRoute.openInGoogleMaps")}
                                    </Button>
                                  )}
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>

                  <Divider />

                  {/* Navigation Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 2,
                    }}
                  >
                    {activeStep === 3 ? (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setActiveStep(0);
                            setSelectedNodeIds([]);
                            setSelectedUserIds([]);
                            setUserLocations({});
                            setRouteResult(null);
                          }}
                          startIcon={<ReplayIcon />}
                        >
                          {t("createRoute.newRoute")}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          onClick={handleBack}
                          disabled={activeStep === 0}
                          startIcon={<ArrowBackIcon />}
                        >
                          {t("createRoute.back")}
                        </Button>

                        {activeStep < 2 ? (
                          <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={isNextDisabled}
                            endIcon={<ArrowForwardIcon />}
                          >
                            {t("createRoute.next")}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={handleCreateRoute}
                            disabled={
                              selectedNodeIds.length === 0 || selectedUserIds.length === 0 || creating
                            }
                            startIcon={
                              creating ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <AltRouteIcon />
                              )
                            }
                            sx={{ px: 4 }}
                          >
                            {t("createRoute.createButton")}
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </Card>
              </Box>
            </Stack>

            {/* Location Edit Dialog */}
            <Dialog
              open={locationDialog.open}
              onClose={() => setLocationDialog({ open: false, node: null })}
              maxWidth="xs"
              fullWidth
              PaperProps={{ sx: { borderRadius: 2 } }}
            >
              <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AddLocationIcon color="primary" />
                {t("createRoute.addLocation")} - {locationDialog.node?.name}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    label={t("createRoute.latitude")}
                    value={editLat}
                    onChange={(e) => setEditLat(e.target.value)}
                    fullWidth
                    margin="normal"
                    type="number"
                    placeholder="39.9334"
                    size="small"
                  />
                  <TextField
                    label={t("createRoute.longitude")}
                    value={editLng}
                    onChange={(e) => setEditLng(e.target.value)}
                    fullWidth
                    margin="normal"
                    type="number"
                    placeholder="32.8597"
                    size="small"
                  />
                  <IconButton
                    color="primary"
                    onClick={handleOpenMap}
                    sx={{
                      mt: 1,
                      border: 1,
                      borderColor: "primary.main",
                      "&:hover": { backgroundColor: "primary.main", color: "white" },
                    }}
                    title={t("createRoute.selectFromMap")}
                  >
                    <MapIcon />
                  </IconButton>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() =>
                    setLocationDialog({ open: false, node: null })
                  }
                >
                  {t("createRoute.back")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveLocation}
                  disabled={!editLat || !editLng}
                >
                  {t("createRoute.save")}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Map Selection Dialog */}
            <Dialog
              open={mapDialogOpen}
              onClose={() => setMapDialogOpen(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{ sx: { borderRadius: 2 } }}
            >
              <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MapIcon color="primary" />
                {t("createRoute.selectFromMap")}
              </DialogTitle>
              <DialogContent sx={{ p: 0, height: 500, position: "relative" }}>
                {mapDialogOpen && (
                  <MapContainer
                    center={[mapCenter.lat, mapCenter.lng]}
                    zoom={mapZoom}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapClickHandler onLocationSelect={handleMapClick} />
                    <MapCenterUpdater center={mapCenter} zoom={mapZoom} />
                    {tempMarker && (
                      <Marker position={[tempMarker.lat, tempMarker.lng]} />
                    )}
                  </MapContainer>
                )}
                {tempMarker && (
                  <Paper
                    elevation={3}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      left: 12,
                      px: 2,
                      py: 1,
                      zIndex: 1000,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <LocationOnIcon fontSize="small" color="error" />
                    <Typography variant="body2">
                      {tempMarker.lat.toFixed(6)}, {tempMarker.lng.toFixed(6)}
                    </Typography>
                  </Paper>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setMapDialogOpen(false)}>
                  {t("createRoute.back")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmMapLocation}
                  disabled={!tempMarker}
                  startIcon={<CheckCircleOutlineIcon />}
                >
                  {t("createRoute.confirmLocation")}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Add Temp User Dialog */}
            <Dialog
              open={tempUserDialog}
              onClose={() => setTempUserDialog(false)}
              maxWidth="xs"
              fullWidth
              PaperProps={{ sx: { borderRadius: 2 } }}
            >
              <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonAddIcon color="primary" />
                {t("createRoute.addUser")}
              </DialogTitle>
              <DialogContent>
                <TextField
                  label={t("createRoute.fullName")}
                  value={tempUserName}
                  onChange={(e) => setTempUserName(e.target.value)}
                  fullWidth
                  margin="normal"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddTempUser();
                  }}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setTempUserDialog(false)}>
                  {t("createRoute.back")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddTempUser}
                  disabled={!tempUserName.trim()}
                  startIcon={<PersonAddIcon />}
                >
                  {t("createRoute.addUser")}
                </Button>
              </DialogActions>
            </Dialog>
          </PageErrorBoundary>
        </Box>
      </Box>
    </AppTheme>
  );
}
